import React, { createContext, useContext, useMemo } from 'react';

import Modules from '../shared_modules/Modules';
import { TrackableProperties, TrackingConfig } from '@lightspeed-payments/payments-portal-sdk';
import { datadogRum } from 'root/datadog/datadogRum';

import { EventType, useTrackingModuleInit } from './hooks/useTrackingModuleInit';

interface TrackingContextValue {
  trackEvent: (event: string, properties?: TrackableProperties, callback?: () => void) => void;
  identify: (merchantId: string, properties: TrackableProperties, callback?: () => void) => void;
}

interface TrackingProviderProps {
  children: React.ReactNode;
  configs: TrackingConfig[];
  fallbackRender?: () => React.ReactNode;
  /**
   * Called once when init fails. Wire this to an error-monitoring service
   * (e.g. `datadogRum.addError`) so silent init failures become visible.
   */
  onInitError?: (error: unknown) => void;
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode; fallbackRender?: () => React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; fallbackRender?: () => React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    datadogRum.addError(error, { context: 'tracking_error_boundary', errorInfo });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallbackRender) {
        return this.props.fallbackRender();
      }
      return null;
    }

    return this.props.children;
  }
}

const TrackingContext = createContext<TrackingContextValue | undefined>(undefined);

export function TrackingProvider({
  children,
  configs,
  fallbackRender,
  onInitError,
}: Readonly<TrackingProviderProps>): JSX.Element {
  const hasReportsCapitalHeapErrorBoundary = Modules.hasModule(
    'reports.capital.heap_error_boundary'
  );
  const { trackingModuleRef, eventQueue, initFailedRef } = useTrackingModuleInit(
    configs,
    onInitError
  );

  const value = useMemo<TrackingContextValue>(() => {
    const dispatchOrQueue = (queued: EventType) => {
      const mod = trackingModuleRef.current;
      if (mod) {
        if (queued.type === 'trackEvent') mod.trackEvent(...queued.payload);
        else mod.identify(...queued.payload);
      } else if (!initFailedRef.current) {
        eventQueue.current.push(queued);
      }
    };

    return {
      trackEvent: (event, properties, callback) =>
        dispatchOrQueue({ type: 'trackEvent', payload: [event, properties, callback] }),
      identify: (merchantId, properties, callback) =>
        dispatchOrQueue({ type: 'identify', payload: [merchantId, properties, callback] }),
    };
  }, [trackingModuleRef, eventQueue, initFailedRef]);

  if (hasReportsCapitalHeapErrorBoundary) {
    return (
      <ErrorBoundary fallbackRender={fallbackRender}>
        <TrackingContext.Provider value={value}>{children}</TrackingContext.Provider>
      </ErrorBoundary>
    );
  }
  return <TrackingContext.Provider value={value}>{children}</TrackingContext.Provider>;
}

export const useTracking = (): TrackingContextValue => {
  const ctx = useContext(TrackingContext);
  if (ctx === undefined) {
    throw new Error('useTracking must be used within a TrackingProvider');
  }
  return ctx;
};
